import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Checkbox selection for a list page (pair with `BulkActionBar`).
 *
 * `resetKey` clears the selection whenever it changes — pass the list's
 * search/filter/sort signature, plus anything selectability depends on (e.g.
 * the acting-as org), so a selection never outlives the rows it was made on.
 * Select-all works on the ids you pass (the loaded rows); there is no
 * "every row matching the filter" mode.
 */
export function useRowSelection(resetKey?: string) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setSelected(new Set());
  }, [resetKey]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const setMany = useCallback((ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  const areAllSelected = useCallback(
    (ids: string[]) => ids.length > 0 && ids.every((id) => selected.has(id)),
    [selected]
  );

  return {
    selected,
    selectedIds,
    count: selected.size,
    toggle,
    setMany,
    clear,
    areAllSelected,
  };
}
